// The screen's own copy of each dataset it shows, kept in the browser's
// IndexedDB so the next open starts from it and asks only for what changed.
//
// Promise:
// - One database (`trameria-datasets`): `rows` keyed by [datasetId, _id] and
//   `meta` keyed by datasetId ({ seq, rev, dto }).
// - A save is all or nothing (one transaction): the rows and the seq they are
//   up to never disagree.
// - Signing out removes the whole database (`clearDatasetCopies`): the next
//   person must not see the previous one's rows.
// - Where IndexedDB is missing (tests, an old browser) nothing is kept; the
//   screen syncs from nothing every time it opens.
import type { DatasetDTO, DatasetRow } from "@trameria/core/datasets-shared";

export interface DatasetCopyMeta {
  seq: number;
  rev: number;
  dto: DatasetDTO | null;
}

export interface StoredDataset {
  meta: DatasetCopyMeta;
  rows: DatasetRow[];
}

export interface DatasetCopyChange {
  meta: DatasetCopyMeta;
  /** The rows replace every kept row (a reset). */
  replace?: Iterable<DatasetRow>;
  put?: Iterable<DatasetRow>;
  delete?: Iterable<string>;
}

export interface DatasetCopies {
  load(id: string): Promise<StoredDataset | null>;
  save(id: string, change: DatasetCopyChange): Promise<void>;
  /** Forgets one dataset (it is gone or no longer seen). */
  remove(id: string): Promise<void>;
}

const DB_NAME = "trameria-datasets";
const ROWS = "rows";
const META = "meta";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(ROWS);
      req.result.createObjectStore(META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("blocked"));
  });
}

/** Every row key of one dataset ([id, string] sorts below [id, []]). */
const rowsOf = (id: string) => IDBKeyRange.bound([id], [id, []]);

const done = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("aborted"));
  });

const result = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

function indexedDbCopies(): DatasetCopies {
  const withDb = async <T>(fn: (db: IDBDatabase) => Promise<T>) => {
    const db = await open();
    try {
      return await fn(db);
    } finally {
      db.close();
    }
  };
  return {
    load: (id) =>
      withDb(async (db) => {
        const tx = db.transaction([ROWS, META], "readonly");
        const [meta, rows] = await Promise.all([
          result(tx.objectStore(META).get(id)),
          result(tx.objectStore(ROWS).getAll(rowsOf(id))),
        ]);
        return meta ? { meta: meta as DatasetCopyMeta, rows } : null;
      }),
    save: (id, change) =>
      withDb(async (db) => {
        const tx = db.transaction([ROWS, META], "readwrite");
        const rows = tx.objectStore(ROWS);
        if (change.replace) {
          rows.delete(rowsOf(id));
          for (const r of change.replace) rows.put(r, [id, r._id]);
        }
        for (const rowId of change.delete ?? []) rows.delete([id, rowId]);
        for (const r of change.put ?? []) rows.put(r, [id, r._id]);
        tx.objectStore(META).put(change.meta, id);
        await done(tx);
      }),
    remove: (id) =>
      withDb(async (db) => {
        const tx = db.transaction([ROWS, META], "readwrite");
        tx.objectStore(ROWS).delete(rowsOf(id));
        tx.objectStore(META).delete(id);
        await done(tx);
      }),
  };
}

const NONE: DatasetCopies = {
  load: async () => null,
  save: async () => {},
  remove: async () => {},
};

/** The browser's copies, or none where IndexedDB is missing. */
export function datasetCopies(): DatasetCopies {
  return typeof indexedDB === "undefined" ? NONE : indexedDbCopies();
}

/** Copies held in memory (tests, and a place that keeps nothing across reloads). */
export function memoryDatasetCopies(): DatasetCopies & {
  stored: Map<string, StoredDataset>;
} {
  const stored = new Map<string, StoredDataset>();
  return {
    stored,
    async load(id) {
      const s = stored.get(id);
      return s ? { meta: s.meta, rows: [...s.rows] } : null;
    },
    async save(id, change) {
      const rows = new Map(
        change.replace
          ? [...change.replace].map((r) => [r._id, r] as const)
          : (stored.get(id)?.rows ?? []).map((r) => [r._id, r] as const),
      );
      for (const rowId of change.delete ?? []) rows.delete(rowId);
      for (const r of change.put ?? []) rows.set(r._id, r);
      stored.set(id, { meta: change.meta, rows: [...rows.values()] });
    },
    async remove(id) {
      stored.delete(id);
    },
  };
}

/** Removes every kept dataset (signing out). Never throws. */
export async function clearDatasetCopies(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    // Another tab holds it open: it is deleted once that tab lets go.
    req.onblocked = () => resolve();
  });
}
